"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { Bold, Italic, Underline, List, ListOrdered } from "lucide-react";
import { Button } from "@/components/ui/button";

interface AddressEditorV2Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export default function AddressEditorV2({
  value,
  onChange,
  placeholder,
}: AddressEditorV2Props) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const skipNextInput = useRef(false);

  // Force LTR on mount and whenever focus changes
  useEffect(() => {
    if (editorRef.current) {
      // Force all possible LTR attributes
      editorRef.current.setAttribute("dir", "ltr");
      editorRef.current.style.direction = "ltr";
      editorRef.current.style.textAlign = "left";

      // Also set it on the document temporarily when focused
      if (isFocused) {
        const originalDir = document.documentElement.dir;
        document.documentElement.dir = "ltr";
        return () => {
          document.documentElement.dir = originalDir;
        };
      }
    }
  }, [isFocused]);

  // Set initial content
  useEffect(() => {
    if (editorRef.current && !isFocused) {
      if (editorRef.current.innerHTML !== value) {
        skipNextInput.current = true;
        editorRef.current.innerHTML = value || "";
      }
    }
  }, [value, isFocused]);

  const handleInput = useCallback(() => {
    if (skipNextInput.current) {
      skipNextInput.current = false;
      return;
    }

    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  }, [onChange]);

  const handleFormat = useCallback((command: string, value?: string) => {
    const selection = window.getSelection();

    if (!selection?.rangeCount || !editorRef.current) return;

    // Special handling for list commands to ensure they work properly
    if (command === "insertUnorderedList" || command === "insertOrderedList") {
      try {
        const range = selection.getRangeAt(0);
        const listTag = command === "insertOrderedList" ? "ol" : "ul";

        // Get the text content at cursor or selection
        let textContent = range.toString();

        if (!textContent) {
          // No selection: create empty list item at cursor
          const listElement = document.createElement(listTag);
          const listItem = document.createElement("li");
          listItem.appendChild(document.createTextNode("\u200B")); // Zero-width space
          listElement.appendChild(listItem);

          range.insertNode(listElement);

          // Move cursor into the list item
          const newRange = document.createRange();
          newRange.setStart(listItem.firstChild || listItem, 0);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
        } else {
          // Text is selected: wrap selection in list
          const listElement = document.createElement(listTag);

          // Split selected text into lines and create list items
          const lines = textContent.split("\n");
          lines.forEach((line) => {
            const trimmedLine = line.trim();
            if (trimmedLine) {
              const listItem = document.createElement("li");
              listItem.appendChild(document.createTextNode(trimmedLine));
              listElement.appendChild(listItem);
            }
          });

          // If no valid lines, create one empty item
          if (listElement.children.length === 0) {
            const listItem = document.createElement("li");
            listItem.appendChild(document.createTextNode("\u200B"));
            listElement.appendChild(listItem);
          }

          // Delete selected content and insert list
          range.deleteContents();
          range.insertNode(listElement);

          // Move cursor to first list item
          const firstItem = listElement.querySelector("li");
          if (firstItem) {
            const newRange = document.createRange();
            newRange.setStart(firstItem.firstChild || firstItem, 0);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        }

        handleInput();
      } catch (error) {
        console.warn("List command failed:", error);
        // Fallback to native command
        try {
          document.execCommand(command, false, value);
          handleInput();
        } catch (e) {
          console.error("Fallback list command also failed:", e);
        }
      }
    } else {
      // Standard formatting commands
      document.execCommand(command, false, value);
      handleInput();
    }

    // Restore focus
    editorRef.current.focus();
  }, [handleInput]);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();

      // Try to get HTML first (from Google Docs, formatted text, etc.)
      let content = e.clipboardData.getData("text/html");

      // Fallback to plain text if HTML is not available
      if (!content) {
        content = e.clipboardData.getData("text/plain");
        if (!content) return;

        // Escape HTML special characters for plain text
        content = content
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;")
          .replace(/\n/g, "<br />");
      } else {
        // Clean up HTML from external sources (Google Docs, Word, etc.)
        // Remove Google Docs specific attributes and styles that might interfere
        content = content
          .replace(/style="[^"]*"/g, "") // Remove inline styles
          .replace(/class="[^"]*"/g, "") // Remove classes
          .replace(/<span[^>]*>/g, "") // Remove span tags but keep content
          .replace(/<\/span>/g, "")
          .replace(/<div[^>]*>/g, "<p>") // Convert divs to paragraphs
          .replace(/<\/div>/g, "</p>")
          .replace(/<p>\s*<\/p>/g, "") // Remove empty paragraphs
          .replace(/<p>/g, "<p>") // Normalize paragraphs
          .trim();

        // Convert paragraphs to line breaks for better readability
        content = content.replace(/<p>(.*?)<\/p>/g, "$1<br />");
      }

      const selection = window.getSelection();
      if (!selection?.rangeCount) return;

      selection.deleteFromDocument();
      const range = selection.getRangeAt(0);

      // Create temporary container to hold the pasted content
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = content;

      // Move all child nodes from temp container into the editor
      const fragment = document.createDocumentFragment();
      while (tempDiv.firstChild) {
        fragment.appendChild(tempDiv.firstChild);
      }

      range.insertNode(fragment);

      // Post-process: Detect and convert numbered paragraphs to lists
      // This handles cases where numbered text is pasted from documents
      const processedHTML = editorRef.current?.innerHTML || "";
      const lines = processedHTML.split("<br />");

      // Check if content looks like a numbered list
      const numberedLinePattern = /^\s*(\d+)\.\s+/;
      const hasNumberedLines = lines.some((line) =>
        numberedLinePattern.test(line.replace(/<[^>]*>/g, ""))
      );

      if (hasNumberedLines) {
        // Convert to proper ordered list
        const listItems: string[] = [];
        lines.forEach((line) => {
          const cleanLine = line.replace(/<[^>]*>/g, "").trim();
          const match = cleanLine.match(numberedLinePattern);
          if (match) {
            const itemText = cleanLine.replace(numberedLinePattern, "");
            listItems.push(`<li>${itemText}</li>`);
          } else if (cleanLine && listItems.length > 0) {
            // Continue previous item if line doesn't have number
            const lastIndex = listItems.length - 1;
            listItems[lastIndex] += ` ${cleanLine}`;
          }
        });

        if (listItems.length > 0) {
          const olElement = document.createElement("ol");
          olElement.innerHTML = listItems.join("");

          // Replace editor content with the new list
          if (editorRef.current) {
            editorRef.current.innerHTML = "";
            editorRef.current.appendChild(olElement);
          }
        }
      }

      // Move cursor to end of inserted content
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);

      handleInput();
    },
    [handleInput],
  );

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Block any RTL keyboard shortcuts
    if (
      (e.ctrlKey || e.metaKey) &&
      e.shiftKey &&
      (e.key === "X" || e.key === "x")
    ) {
      e.preventDefault();
      return;
    }

    // Force LTR for each keystroke
    if (editorRef.current) {
      editorRef.current.dir = "ltr";
      editorRef.current.style.direction = "ltr";
    }
  }, []);

  const handleFocus = useCallback(() => {
    setIsFocused(true);

    // Double-check LTR on focus
    if (editorRef.current) {
      editorRef.current.dir = "ltr";
      editorRef.current.style.direction = "ltr";
      editorRef.current.style.textAlign = "left";
    }
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  return (
    <div className="address-editor-wrapper" style={{ direction: "ltr" }}>
      <style jsx>{`
        .address-editor-content {
          unicode-bidi: isolate !important;
        }
      `}</style>

      <div
        className={`border rounded-md ${isFocused ? "ring-2 ring-ring ring-offset-2" : ""}`}
      >
        <div className="border-b p-2 flex gap-1" dir="ltr">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleFormat("bold")}
            className="h-8 w-8 p-0"
          >
            <Bold className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleFormat("italic")}
            className="h-8 w-8 p-0"
          >
            <Italic className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleFormat("underline")}
            className="h-8 w-8 p-0"
          >
            <Underline className="h-4 w-4" />
          </Button>
          <div className="w-px bg-border mx-1" />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleFormat("insertUnorderedList")}
            className="h-8 w-8 p-0"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleFormat("insertOrderedList")}
            className="h-8 w-8 p-0"
          >
            <ListOrdered className="h-4 w-4" />
          </Button>
        </div>

        <div
          ref={editorRef}
          contentEditable
          className="address-editor-content p-3 min-h-[100px] focus:outline-none"
          dir="ltr"
          lang="en"
          onInput={handleInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          suppressContentEditableWarning={true}
          data-placeholder={placeholder || "Enter venue address..."}
          style={{
            direction: "ltr",
            textAlign: "left",
            unicodeBidi: "isolate",
          }}
        />
      </div>
    </div>
  );
}
